import { Card, Button, Descriptions, Statistic, Row, Col, Typography, Space } from "antd";
import axios from "axios";
import { useState } from "react";

import { showErrorNotification } from "../../../../../utils/notification";
import CreateEditModuleModal from "../../Modals/CreateEditModuleModal";
import CreateEditSetModal from "../../Modals/CreateEditSetModal";
import { formatPreviousSlugs } from "../../SlugFormItem";

const { Title } = Typography;

const ModuleDetails = ({ ROOT_URL, BASE_URL, moduleData, refreshContentTree = () => {} }) => {
  const [activeModal, setActiveModal] = useState(null); // 'MODULE_EDIT', 'LECTURE_SET_CREATE', 'PROBLEM_SET_CREATE'
  const [loading, setLoading] = useState(false);

  // Metadata from API
  const {
    totalLectures = 0,
    totalEasyProblems = 0,
    totalMediumProblems = 0,
    totalHardProblems = 0,
    totalLectureSets = 0,
    totalProblemSets = 0
  } = moduleData;

  const totalProblems = totalEasyProblems + totalMediumProblems + totalHardProblems;

  const handleUpdateModule = (values) => {
    setLoading(true);
    axios
      .post(`${ROOT_URL}/${BASE_URL}/update`, {
        ...values,
        uniquePublicId: moduleData.uniquePublicId
      })
      .then(() => {
        setActiveModal(null);
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCreateLectureSet = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      moduleUniquePublicId: moduleData.uniquePublicId
    };
    axios
      .post(`${ROOT_URL}/${BASE_URL}/lecture-set/create`, payload)
      .then(() => {
        setActiveModal(null);
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCreateProblemSet = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      moduleUniquePublicId: moduleData.uniquePublicId
    };
    axios
      .post(`${ROOT_URL}/${BASE_URL}/problem-set/create`, payload)
      .then(() => {
        setActiveModal(null);
        refreshContentTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isModuleModalOpen = activeModal === "MODULE_EDIT";
  const isSetModalOpen =
    activeModal === "LECTURE_SET_CREATE" || activeModal === "PROBLEM_SET_CREATE";

  const getSetModalProps = () => {
    switch (activeModal) {
      case "LECTURE_SET_CREATE":
        return {
          entityName: "Lecture Set",
          initialValues: null,
          onCreate: handleCreateLectureSet,
          isEdit: false
        };
      case "PROBLEM_SET_CREATE":
        return {
          entityName: "Problem Set",
          initialValues: null,
          onCreate: handleCreateProblemSet,
          isEdit: false
        };
      default:
        return {};
    }
  };

  const setModalProps = getSetModalProps();

  return (
    <>
      <Card
        title={<Title level={4}>{`Module: ${moduleData.title}`}</Title>}
        extra={
          <Space wrap>
            <Button onClick={() => setActiveModal("LECTURE_SET_CREATE")}>+ Lecture Set</Button>
            <Button onClick={() => setActiveModal("PROBLEM_SET_CREATE")}>+ Problem Set</Button>
            <Button type="primary" onClick={() => setActiveModal("MODULE_EDIT")}>
              Edit Module Metadata
            </Button>
          </Space>
        }
      >
        <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }} style={{ marginBottom: 24 }}>
          <Descriptions.Item label="Title">{moduleData.title}</Descriptions.Item>
          <Descriptions.Item label="Slug">{moduleData.slug || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Previous Slugs">
            {formatPreviousSlugs(moduleData)}
          </Descriptions.Item>
        </Descriptions>

        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Card>
              <Statistic title="Lecture Sets" value={totalLectureSets} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic title="Problem Sets" value={totalProblemSets} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic title="Total Lectures" value={totalLectures} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic title="Total Problems" value={totalProblems} />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Easy Problems"
                value={totalEasyProblems}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Medium Problems"
                value={totalMediumProblems}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Hard Problems"
                value={totalHardProblems}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Module Modal */}
      <CreateEditModuleModal
        open={isModuleModalOpen}
        onCancel={() => setActiveModal(null)}
        initialValues={{
          title: moduleData.title,
          slug: moduleData.slug,
          reservedSlugs: moduleData.reservedSlugs,
          bannerImageUrl: moduleData.bannerImageUrl,
          moduleIconImageUrl: moduleData.moduleIconImageUrl,
          uniquePublicId: moduleData.uniquePublicId
        }}
        onUpdate={handleUpdateModule}
        loading={loading}
        ROOT_URL={ROOT_URL}
        BASE_URL={BASE_URL}
      />

      {/* Set Modal */}
      <CreateEditSetModal
        open={isSetModalOpen}
        onCancel={() => setActiveModal(null)}
        loading={loading}
        {...setModalProps}
      />
    </>
  );
};

export default ModuleDetails;
